import { useTranslation } from "react-i18next";

import { Grid, InputAdornment } from "@mui/material";
import { FormikProps } from "formik";

import { FormikDecimalTextField } from "../../../../../../formik/FormikDecimalTextField";
import { FormikTextField } from "../../../../../../formik/FormikTextField";
import { FormikYesNoAutocomplete } from "../../../../../../formik/FormikYesNoAutocomplete";
import { FormikDatePicker } from "../../../../../../formik/formik-date-picker/FormikDatePicker";
import { AssetDocumentArea, PendingDocumentMove } from "../../../../../api/ClientFileApi";
import { IncomeSectionCard } from "../../../income/components/income-section-card/IncomeSectionCard";
import { AssetsFormValues, AssetsMaxLength } from "../../AssetsView.types";
import { ASSET_UPLOAD_AREAS } from "../../AssetsView.utils";
import { AssetUploadArea } from "../asset-upload-area/AssetUploadArea";
import { AssetYesNoQuestion } from "../asset-yes-no-question/AssetYesNoQuestion";

const SECTION = "clients.client.debtSettlement.assetInventory.assetsTab.cash";
const FULL = 12;
const HALF = { xs: 12, md: 6 };

interface Props {
  clientId: string;
  formik: FormikProps<AssetsFormValues>;
  onAddMove: (move: PendingDocumentMove) => void;
  onCancelMove: (documentId: string) => void;
}

export function CashAssetsSection({ clientId, formik, onAddMove, onCancelMove }: Props) {
  const { t } = useTranslation();
  const { answers } = formik.values;
  const hasOtherCashAssets = answers.hasOtherCashAssets === true;

  return (
    <IncomeSectionCard title={t(`${SECTION}.title`)}>
      <AssetYesNoQuestion
        formik={formik}
        field="hasSafeDeposit"
        label={t(`${SECTION}.hasSafeDeposit`)}
        detailsField="safeDepositDetails"
        detailsLabel={t(`${SECTION}.safeDepositDetails`)}
      />
      <AssetYesNoQuestion formik={formik} field="hasOtherCashAssets" label={t(`${SECTION}.hasOtherCashAssets`)} />
      {hasOtherCashAssets && (
        <Grid container spacing={2}>
          <Grid size={FULL}>
            <FormikYesNoAutocomplete
              id="assets-is-saved-for-proceeding"
              formik={formik}
              path="answers.isSavedForProceeding"
              label={t(`${SECTION}.isSavedForProceeding`)}
              disableClearable
            />
          </Grid>
          {answers.isSavedForProceeding === true && (
            <>
              <Grid size={HALF}>
                <FormikDecimalTextField
                  id="assets-saved-for-proceeding-amount"
                  formik={formik}
                  path="answers.savedForProceedingAmount"
                  label={t(`${SECTION}.savedForProceedingAmount`)}
                  decimalScale={2}
                  minValue={0}
                  slotProps={{ input: { endAdornment: <InputAdornment position="end">€</InputAdornment> } }}
                />
              </Grid>
              <Grid size={HALF}>
                <FormikDatePicker
                  formik={formik}
                  path="answers.savedForProceedingDate"
                  label={t(`${SECTION}.savedForProceedingDate`)}
                />
              </Grid>
            </>
          )}
          {answers.isSavedForProceeding === false && (
            <Grid size={FULL}>
              <FormikTextField
                id="assets-other-cash-assets-details"
                formik={formik}
                path="answers.otherCashAssetsDetails"
                label={t(`${SECTION}.otherCashAssetsDetails`)}
                multiline
                minRows={3}
                slotProps={{ input: { inputProps: { maxLength: AssetsMaxLength.details } } }}
              />
            </Grid>
          )}
        </Grid>
      )}
      {ASSET_UPLOAD_AREAS[AssetDocumentArea.CashAndBankAssets].isShown(answers) && (
        <AssetUploadArea
          clientId={clientId}
          formik={formik}
          area={AssetDocumentArea.CashAndBankAssets}
          label={t(`${SECTION}.upload`)}
          onAddMove={onAddMove}
          onCancelMove={onCancelMove}
        />
      )}
    </IncomeSectionCard>
  );
}
